import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { WebSerialPrinterService } from '../../../../../core/services/webserial-printer.service';
import { WebUsbPrinterService } from '../../../../../core/services/webusb-printer.service';
import { SettingsPrintersComponent } from '../../../pages/settings/printers/printers.component';
import { GrantedPort, PrinterDiscoveryService } from '../../../../../core/services/printer-discovery.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-print-receipt-button',
  templateUrl: './print-receipt-button.component.html',
  styleUrls: ['./print-receipt-button.component.scss']
})
export class PrintReceiptButtonComponent implements OnInit, OnDestroy, OnChanges {
  @Input() label: string = 'Talao de controlo';
  @Input() icon: string = 'heroicons_outline:receipt-tax';
  @Input() disabled: boolean = false;
  @Input() color: 'primary' | 'accent' | 'warn' | undefined = 'primary';
  @Input() buttonId: string = 'print-receipt-button';
  // Optional preselected printer to be set by parent components (e.g., POS Detail)
  @Input() preselectedPrinter?: { vendorId: number | null; productId: number | null } | null;

  @Output() print = new EventEmitter<void>();

  selectedPrinterLabel: string = 'Sem impressora';
  isMobileResolution: boolean;
  printers: GrantedPort[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private _matDialog: MatDialog,
    private wsPrinter: WebSerialPrinterService,
    private usbPrinter: WebUsbPrinterService,
    private discovery: PrinterDiscoveryService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preselectedPrinter']) {
      const printer = this.preselectedPrinter;
      if (printer && (printer.vendorId != null || printer.productId != null)) {
        try {
          const transport = (localStorage.getItem('printerTransport') as 'serial' | 'usb' | null) || 'serial';
          const service = transport === 'usb' ? this.usbPrinter : this.wsPrinter;
          service.setDefaultPrinter({ vendorId: printer.vendorId, productId: printer.productId });
        } catch {
          // ignore
        }
      }
      // Update label regardless
      this.updateSelectedPrinterLabel();
    }
  }

  ngOnInit(): void {
    this.updateMobileResolution();

    this.discovery.printers$.pipe(takeUntil(this.destroy$)).subscribe(list => {
      this.printers = list;
      this.updateSelectedPrinterLabel();
    });

    this.updateSelectedPrinterLabel();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateMobileResolution();
  }

  private updateMobileResolution(): void {
    this.isMobileResolution = window.innerWidth < 720;
  }

  openPrinterSelector(): void {
    const dialogRef = this._matDialog.open(SettingsPrintersComponent, {
      width: this.isMobileResolution ? '100vw' : '720px',
      height: this.isMobileResolution ? '100vh' : 'auto',
      panelClass: this.isMobileResolution ? 'receiptOnModal' : 'receiptOnModal-desktop',
      data: { selectionMode: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && (result.vendorId != null || result.productId != null)) {
        try {
          if (result.transport) {
            localStorage.setItem('printerTransport', result.transport);
          }
          const transport = result.transport || (localStorage.getItem('printerTransport') as 'serial' | 'usb' | null) || 'serial';
          const service = transport === 'usb' ? this.usbPrinter : this.wsPrinter;
          service.setDefaultPrinter({ vendorId: result.vendorId, productId: result.productId });
        } catch (e) {
          // ignore
        }
        this.updateSelectedPrinterLabel();
      }
    });
  }

  choosePrinter(p: GrantedPort): void {
    const transport = p.type || 'serial';
    localStorage.setItem('printerTransport', transport);
    const service = transport === 'usb' ? this.usbPrinter : this.wsPrinter;
    try {
      service.setDefaultPrinter({ vendorId: p.info.usbVendorId, productId: p.info.usbProductId });
    } catch {
      // ignore
    }
    this.updateSelectedPrinterLabel();
  }

  isSelected(p: GrantedPort): boolean {
    const transport = (localStorage.getItem('printerTransport') as 'serial' | 'usb' | null) || 'serial';
    if (p.type && p.type !== transport) return false;

    const service = transport === 'usb' ? this.usbPrinter : this.wsPrinter;
    const dev = service.lastDevice;
    return !!dev && dev.vendorId === p.info.usbVendorId && dev.productId === p.info.usbProductId;
  }

  getPrinterName(p: GrantedPort): string {
    const adapted = { vendorId: p.info.usbVendorId, productId: p.info.usbProductId, productName: p.info.productName };
    const service = p.type === 'usb' ? this.usbPrinter : this.wsPrinter;
    return service.identifyPrinter(adapted as any);
  }

  private async updateSelectedPrinterLabel(): Promise<void> {
    const transport = (localStorage.getItem('printerTransport') as 'serial' | 'usb' | null) || 'serial';
    const service = transport === 'usb' ? this.usbPrinter : this.wsPrinter;

    const dev = service.lastDevice || this.preselectedPrinter;
    const allPrinters = this.printers.length ? this.printers : await this.discovery.refresh().then(() => this.discovery.snapshot);

    if (dev && ((dev as any).vendorId != null || (dev as any).productId != null)) {
      try {
        // Try to find the printer in discovered list to get full info (productName, etc)
        const match = allPrinters.find(p => p.info.usbVendorId === (dev as any).vendorId && p.info.usbProductId === (dev as any).productId);
        if (match) {
          this.selectedPrinterLabel = this.getPrinterName(match);
        } else {
          this.selectedPrinterLabel = service.identifyPrinter(dev as any);
        }
      } catch {
        this.selectedPrinterLabel = 'Impressora selecionada';
      }
      return;
    }

    // Fallback to discovery list to decide label
    const transportPrinters = allPrinters.filter(p => p.type === transport);
    if (transportPrinters.length) {
      this.selectedPrinterLabel = 'Impressora disponível';
    } else {
      this.selectedPrinterLabel = 'Sem impressora';
    }
  }
}
